import React, {
  CSSProperties,
  FC,
  KeyboardEventHandler,
  MouseEventHandler,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { isEmpty } from 'ramda';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { SelectFieldOption, Slide } from '@/eventConfig/types.ts';
import { useFieldValidation } from '@/form/fields/_pipeline/useValidateField.ts';
import { useUiEvent } from '@/util/useUiEvent.ts';
import useSlideIcons from '@/util/slideIcons.ts';
import { translate } from '@/translation/translate.ts';
import { normalize } from '@/form/fields/_pipeline/normalize.ts';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { useBmiValidation } from '@/util/useBmiValidation.ts';
import { useRevalidateField } from '@/util/useRevalidateField.ts';
import { isBmiSlide } from '@/form/utils.tsx';
import { resolveSelectOptions, SelectProps } from '@/form/fields/SelectInput/SelectInput.tsx';

interface FlatOption {
  label: string;
  value: string;
}

interface MenuPosition {
  top: number;
  left: number;
  width: number;
  openUpwards: boolean;
}

const MENU_GAP = 4;
const MENU_OPEN_THRESHOLD = 220;

const flattenOptions = (options: SelectFieldOption[]): FlatOption[] => {
  return options.flatMap((option) => {
    if (option.items?.length) {
      return flattenOptions(option.items);
    }

    const value = String(option.value ?? '');
    if (value === '-' || value === '--') {
      return [];
    }

    return [{ value, label: translate(option.label) }];
  });
};

export const FilterSelectInput: FC<SelectProps> = ({ field, slideId, getFieldStatus, onChange, getFieldValue }) => {
  const { eventConfig } = useEventConfig();
  const { handleUiEvent } = useUiEvent();
  const { infoIcon } = useSlideIcons({} as Slide);
  const { validateField } = useFieldValidation(getFieldValue, getFieldStatus);
  const { isValidBmi } = useBmiValidation(getFieldValue);
  const { revalidateFields } = useRevalidateField(validateField);

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const activeOptionRef = useRef<HTMLLIElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);
  const hasUserInteractedRef = useRef(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const prevValue = useRef<string>(String(getFieldStatus(field.name)?.value?.raw ?? ''));

  const fieldStatus = getFieldStatus(field.name);
  const fieldHint = fieldStatus?.hint;
  const isValid = fieldStatus?.isValid ?? true;
  const value = String(fieldStatus?.value?.raw ?? '');
  const htmlId = field.name;
  const validators = field.validator || [];
  const isRequired = validators.find((v) => v.type === 'required' || v.type === 'required_if');

  const { options, hasNoOptions } = resolveSelectOptions(field, eventConfig!.meta.defaultLocale, getFieldStatus);
  const flatOptions = useMemo(() => flattenOptions(options), [options]);

  const selectedOption = flatOptions.find((option) => option.value === value);
  const normalizedQuery = query.trim().toLowerCase();

  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) {
      return flatOptions;
    }

    return flatOptions.filter((option) => option.label.toLowerCase().includes(normalizedQuery));
  }, [flatOptions, normalizedQuery]);

  useEffect(() => {
    if (activeIndex >= filteredOptions.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, filteredOptions.length]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    activeOptionRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen, filteredOptions.length]);

  useEffect(() => {
    const markUserInteraction = () => {
      hasUserInteractedRef.current = true;
    };

    window.addEventListener('pointerdown', markUserInteraction, true);
    window.addEventListener('keydown', markUserInteraction, true);

    return () => {
      window.removeEventListener('pointerdown', markUserInteraction, true);
      window.removeEventListener('keydown', markUserInteraction, true);
    };
  }, []);

  useEffect(() => {
    if (field.revalidate && value != prevValue.current) {
      prevValue.current = value;
      revalidateFields(field.revalidate);
    }
  }, [field.revalidate, revalidateFields, value]);

  useEffect(() => {
    if (!isBmiSlide(slideId)) {
      return;
    }

    const raw = value;
    const normalized = normalize(field.datatype, raw);
    const fieldValue = { raw, normalized };
    const { hint, isValid } = validateField(field.validator, fieldValue);
    onChange(field.name, { value: fieldValue, hint, isValid, slideId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidBmi]);

  useEffect(() => {
    const gfs = getFieldStatus(field.name);
    const fieldValue = gfs?.value ?? { raw: null, normalized: null };
    const { hint, isValid } = validateField(field.validator, fieldValue);
    onChange(field.name, { value: fieldValue, hint, isValid, slideId });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const anchor = controlRef.current;
    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpwards = spaceBelow < MENU_OPEN_THRESHOLD && spaceAbove > spaceBelow;

    setMenuPosition({
      top: window.scrollY + (openUpwards ? rect.top - MENU_GAP : rect.bottom + MENU_GAP),
      left: window.scrollX + rect.left,
      width: rect.width,
      openUpwards,
    });
  }, [isOpen, query, filteredOptions.length]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const updateMenuPosition = () => {
      const anchor = controlRef.current;
      if (!anchor) {
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpwards = spaceBelow < MENU_OPEN_THRESHOLD && spaceAbove > spaceBelow;

      setMenuPosition({
        top: window.scrollY + (openUpwards ? rect.top - MENU_GAP : rect.bottom + MENU_GAP),
        left: window.scrollX + rect.left,
        width: rect.width,
        openUpwards,
      });
    };

    const onOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInRoot = !!rootRef.current?.contains(target);
      const isInMenu = !!menuRef.current?.contains(target);
      if (!isInRoot && !isInMenu) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    document.addEventListener('mousedown', onOutsideClick);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
      document.removeEventListener('mousedown', onOutsideClick);
    };
  }, [isOpen]);

  const handleSelect = (raw: string) => {
    const normalized = normalize(field.datatype, raw);
    const fieldValue = { raw, normalized };
    const { hint, isValid } = validateField(field.validator, fieldValue);
    onChange(field.name, { value: fieldValue, hint, isValid, slideId });
    setQuery('');
    setIsOpen(false);
  };

  const closeIfInactive = () => {
    // Run after focus settles so Tab/click target is available in document.activeElement.
    setTimeout(() => {
      const activeElement = document.activeElement;
      const isInRoot = !!(activeElement && rootRef.current?.contains(activeElement));
      const isInMenu = !!(activeElement && menuRef.current?.contains(activeElement));
      if (!isInRoot && !isInMenu) {
        setIsOpen(false);
      }
    }, 0);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (!isOpen && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      setIsOpen(true);
      if (event.key === 'ArrowUp') {
        setActiveIndex(Math.max(filteredOptions.length - 1, -1));
      } else {
        setActiveIndex(filteredOptions.length ? 0 : -1);
      }
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => {
        const count = filteredOptions.length;
        if (!count) {
          return -1;
        }

        return index < 0 ? 0 : (index + 1) % count;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => {
        const count = filteredOptions.length;
        if (!count) {
          return -1;
        }

        return index < 0 ? count - 1 : (index - 1 + count) % count;
      });
      return;
    }

    if (event.key === 'Enter' && filteredOptions[activeIndex]) {
      event.preventDefault();
      handleSelect(filteredOptions[activeIndex].value);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
      setQuery('');
    }
  };

  const handleLabelClick: MouseEventHandler<HTMLLabelElement> = (_e) => {
    if (field.click && handleUiEvent) {
      handleUiEvent(field.click);
    }
  };

  const handleInfoClick: MouseEventHandler<HTMLLabelElement> = (_e) => {
    handleUiEvent({
      type: 'layer',
      text: field?.info?.text,
      title: field?.info?.title,
    });
  };

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
    'state-' + (isValid ? 'valid' : 'invalid'),
  ];

  const hint = fromTemplate(fieldHint || field?.hint || '');
  const hasInfoLayer = !!field.info;

  const infoIconStyles: CSSProperties = {
    position: 'unset',
    marginRight: '0.5rem',
    cursor: 'pointer',
  };

  const inputValue = isOpen ? query : selectedOption?.label || '';
  const listboxId = htmlId + '-listbox';

  const optionsMenu =
    isOpen && !hasNoOptions && menuPosition
      ? createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            id={listboxId}
            className={'filter-select-options' + (menuPosition.openUpwards ? ' open-upwards' : '')}
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              transform: menuPosition.openUpwards ? 'translateY(calc(-100% - 8px))' : undefined,
            }}
          >
            {filteredOptions.length ? (
              filteredOptions.map((option, index) => {
                const activeClass = index === activeIndex ? 'active' : '';
                return (
                  <li
                    ref={index === activeIndex ? activeOptionRef : undefined}
                    key={option.value + '-' + index}
                    role="option"
                    aria-selected={option.value === value}
                    className={activeClass}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleSelect(option.value);
                    }}
                  >
                    {option.label}
                  </li>
                );
              })
            ) : (
              <li className="empty-results">No results</li>
            )}
          </ul>,
          document.body
        )
      : null;

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type + (isOpen ? ' is-open' : '')} ref={rootRef}>
        <label htmlFor={htmlId} className={isRequired ? 'required' : ''} onClick={handleLabelClick}>
          {hasInfoLayer && (
            <span onClick={handleInfoClick}>
              <i className={infoIcon} style={infoIconStyles} />
            </span>
          )}
          <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
            {translate(field.label)}
          </ReactMarkdown>
        </label>

        <div className="filter-select-control" ref={controlRef}>
          <input
            id={htmlId}
            type="text"
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            autoComplete="off"
            value={inputValue}
            className={!isEmpty(value) ? 'populated' : 'empty'}
            disabled={hasNoOptions}
            onFocus={() => {
              // Defer to keep full selection even after click focus default handling.
              requestAnimationFrame(() => inputRef.current?.select());

              if (!hasUserInteractedRef.current) {
                return;
              }

              setIsOpen(true);
              setActiveIndex(filteredOptions.length ? 0 : -1);
            }}
            onClick={() => {
              setIsOpen(true);
              setActiveIndex(filteredOptions.length ? 0 : -1);
            }}
            onBlur={closeIfInactive}
            onKeyDown={handleKeyDown}
            onChange={(event) => {
              setQuery(event.target.value);
              setIsOpen(true);
              setActiveIndex(0);
            }}
            placeholder={String(field.placeholder || '')}
            ref={inputRef}
          />
          <input type="hidden" name={field.name} value={value} />
        </div>
        {optionsMenu}
        <i className="fas fa-list" />
      </div>
      <div className={hint ? 'hint message' : 'hint'}>{hint}</div>
    </div>
  );
};
